import { InboxProps } from "@novu/react";

// Static texts of the Novu inbox overlay per UI language. The notification
// contents themselves are maintained in Novu. Not localizable here: the channel
// names in Novu's own preferences view and the "Inbox by Novu" branding.
type NovuLocalization = NonNullable<InboxProps["localization"]>;

const novuLocalizationDe: NovuLocalization = {
    "inbox.filters.dropdownOptions.unread": "Nur ungelesen",
    "inbox.filters.dropdownOptions.default": "Ungelesen & gelesen",
    "inbox.filters.dropdownOptions.archived": "Archiviert",
    "inbox.filters.dropdownOptions.snoozed": "Zurückgestellt",
    "inbox.filters.labels.unread": "Ungelesen",
    "inbox.filters.labels.default": "Posteingang",
    "inbox.filters.labels.archived": "Archiviert",
    "inbox.filters.labels.snoozed": "Zurückgestellt",
    "notifications.emptyNotice": "Im Moment ist es ruhig. Schauen Sie später wieder vorbei.",
    "notifications.actions.readAll": "Alle als gelesen markieren",
    "notifications.actions.archiveAll": "Alle archivieren",
    "notifications.actions.archiveRead": "Gelesene archivieren",
    "notifications.newNotifications": ({ notificationCount }) =>
        `${notificationCount} neue Benachrichtigung${notificationCount === 1 ? "" : "en"}`,
    "notification.actions.read.tooltip": "Als gelesen markieren",
    "notification.actions.unread.tooltip": "Als ungelesen markieren",
    "notification.actions.archive.tooltip": "Archivieren",
    "notification.actions.unarchive.tooltip": "Aus dem Archiv entfernen",
    "notification.actions.snooze.tooltip": "Zurückstellen",
    "notification.actions.unsnooze.tooltip": "Nicht mehr zurückstellen",
    "notification.snoozedUntil": "Zurückgestellt bis",
    "preferences.title": "Einstellungen",
    "preferences.emptyNotice": "Noch keine spezifischen Benachrichtigungseinstellungen.",
    "preferences.global": "Globale Einstellungen",
    "preferences.schedule.title": "Zeitplan",
    "preferences.schedule.description": "Benachrichtigungen erlauben zwischen:",
    "preferences.schedule.headerInfo":
        "Legen Sie Ihren Zeitplan fest. Benachrichtigungen über externe Kanäle werden außerhalb des Zeitplans pausiert. In-App- und kritische Benachrichtigungen werden immer zugestellt.",
    "preferences.schedule.info":
        "Kritische und In-App-Benachrichtigungen erreichen Sie auch außerhalb Ihres Zeitplans.",
    "preferences.schedule.days": "Tage",
    "preferences.schedule.from": "Von",
    "preferences.schedule.to": "Bis",
    "preferences.schedule.copyTimesTo": "Zeiten kopieren auf",
    "preferences.schedule.sunday": "Sonntag",
    "preferences.schedule.monday": "Montag",
    "preferences.schedule.tuesday": "Dienstag",
    "preferences.schedule.wednesday": "Mittwoch",
    "preferences.schedule.thursday": "Donnerstag",
    "preferences.schedule.friday": "Freitag",
    "preferences.schedule.saturday": "Samstag",
    "preferences.schedule.dayScheduleCopy.title": "Zeiten kopieren auf:",
    "preferences.schedule.dayScheduleCopy.selectAll": "Alle auswählen",
    "preferences.schedule.dayScheduleCopy.apply": "Übernehmen",
    "preferences.workflow.disabled.notice":
        "Wenden Sie sich an den Administrator, um die Verwaltung dieser kritischen Benachrichtigung zu aktivieren.",
    "preferences.workflow.disabled.tooltip": "Bearbeitung nur durch Administrator",
    "preferences.group.info": "Gilt für alle Benachrichtigungen in dieser Gruppe.",
    "snooze.datePicker.timePickerLabel": "Uhrzeit",
    "snooze.datePicker.apply": "Übernehmen",
    "snooze.datePicker.cancel": "Abbrechen",
    "snooze.options.anHourFromNow": "In einer Stunde",
    "snooze.datePicker.pastDateTooltip": "Die ausgewählte Zeit muss mindestens 3 Minuten in der Zukunft liegen.",
    "snooze.datePicker.noDateSelectedTooltip": "Bitte wählen Sie ein Datum aus.",
    "snooze.datePicker.exceedingLimitTooltip": ({ days }) =>
        `Die ausgewählte Zeit darf höchstens ${days} Tage in der Zukunft liegen.`,
    "snooze.options.customTime": "Benutzerdefinierte Zeit ...",
    "snooze.options.inOneDay": "Morgen",
    "snooze.options.inOneWeek": "Nächste Woche",
    locale: "de-DE",
};

const novuLocalizationFr: NovuLocalization = {
    "inbox.filters.dropdownOptions.unread": "Non lues uniquement",
    "inbox.filters.dropdownOptions.default": "Non lues et lues",
    "inbox.filters.dropdownOptions.archived": "Archivées",
    "inbox.filters.dropdownOptions.snoozed": "Reportées",
    "inbox.filters.labels.unread": "Non lues",
    "inbox.filters.labels.default": "Boîte de réception",
    "inbox.filters.labels.archived": "Archivées",
    "inbox.filters.labels.snoozed": "Reportées",
    "notifications.emptyNotice": "Rien de nouveau pour le moment. Revenez plus tard.",
    "notifications.actions.readAll": "Tout marquer comme lu",
    "notifications.actions.archiveAll": "Tout archiver",
    "notifications.actions.archiveRead": "Archiver les notifications lues",
    "notifications.newNotifications": ({ notificationCount }) =>
        notificationCount === 1 ? "1 nouvelle notification" : `${notificationCount > 99 ? "99+" : notificationCount} nouvelles notifications`,
    "notification.actions.read.tooltip": "Marquer comme lu",
    "notification.actions.unread.tooltip": "Marquer comme non lu",
    "notification.actions.archive.tooltip": "Archiver",
    "notification.actions.unarchive.tooltip": "Désarchiver",
    "notification.actions.snooze.tooltip": "Reporter",
    "notification.actions.unsnooze.tooltip": "Annuler le report",
    "notification.snoozedUntil": "Reportée jusqu’au",
    "preferences.title": "Préférences",
    "preferences.emptyNotice": "Aucune préférence de notification spécifique pour le moment.",
    "preferences.global": "Préférences globales",
    "preferences.schedule.title": "Calendrier",
    "preferences.schedule.description": "Autoriser les notifications entre :",
    "preferences.schedule.headerInfo":
        "Définissez votre calendrier. Les notifications sur les canaux externes sont suspendues en dehors du calendrier. Les notifications dans l’application et les notifications critiques sont toujours envoyées.",
    "preferences.schedule.info":
        "Les notifications critiques et dans l’application vous parviennent également en dehors de votre calendrier.",
    "preferences.schedule.days": "Jours",
    "preferences.schedule.from": "De",
    "preferences.schedule.to": "À",
    "preferences.schedule.copyTimesTo": "Copier les horaires vers",
    "preferences.schedule.sunday": "Dimanche",
    "preferences.schedule.monday": "Lundi",
    "preferences.schedule.tuesday": "Mardi",
    "preferences.schedule.wednesday": "Mercredi",
    "preferences.schedule.thursday": "Jeudi",
    "preferences.schedule.friday": "Vendredi",
    "preferences.schedule.saturday": "Samedi",
    "preferences.schedule.dayScheduleCopy.title": "Copier les horaires vers :",
    "preferences.schedule.dayScheduleCopy.selectAll": "Tout sélectionner",
    "preferences.schedule.dayScheduleCopy.apply": "Appliquer",
    "preferences.workflow.disabled.notice":
        "Contactez l’administrateur pour activer la gestion de cette notification critique.",
    "preferences.workflow.disabled.tooltip": "Modification réservée à l’administrateur",
    "preferences.group.info": "S’applique à toutes les notifications de ce groupe.",
    "snooze.datePicker.timePickerLabel": "Heure",
    "snooze.datePicker.apply": "Appliquer",
    "snooze.datePicker.cancel": "Annuler",
    "snooze.options.anHourFromNow": "Dans une heure",
    "snooze.datePicker.pastDateTooltip": "L’heure sélectionnée doit se situer au moins 3 minutes dans le futur.",
    "snooze.datePicker.noDateSelectedTooltip": "Veuillez sélectionner une date.",
    "snooze.datePicker.exceedingLimitTooltip": ({ days }) =>
        `L’heure sélectionnée ne peut pas dépasser ${days === 1 ? "24 heures" : `${days} jours`} à partir de maintenant.`,
    "snooze.options.customTime": "Heure personnalisée…",
    "snooze.options.inOneDay": "Demain",
    "snooze.options.inOneWeek": "La semaine prochaine",
    locale: "fr-FR",
};

const novuLocalizationKo: NovuLocalization = {
    "inbox.filters.dropdownOptions.unread": "읽지 않은 알림만",
    "inbox.filters.dropdownOptions.default": "읽지 않은 알림 및 읽은 알림",
    "inbox.filters.dropdownOptions.archived": "보관됨",
    "inbox.filters.dropdownOptions.snoozed": "다시 알림 설정됨",
    "inbox.filters.labels.unread": "읽지 않음",
    "inbox.filters.labels.default": "받은 알림",
    "inbox.filters.labels.archived": "보관됨",
    "inbox.filters.labels.snoozed": "다시 알림 설정됨",
    "notifications.emptyNotice": "현재 새 알림이 없습니다. 나중에 다시 확인하세요.",
    "notifications.actions.readAll": "모두 읽음으로 표시",
    "notifications.actions.archiveAll": "모두 보관",
    "notifications.actions.archiveRead": "읽은 알림 보관",
    "notifications.newNotifications": ({ notificationCount }) =>
        `새 알림 ${notificationCount > 99 ? "99+" : notificationCount}개`,
    "notification.actions.read.tooltip": "읽음으로 표시",
    "notification.actions.unread.tooltip": "읽지 않음으로 표시",
    "notification.actions.archive.tooltip": "보관",
    "notification.actions.unarchive.tooltip": "보관 해제",
    "notification.actions.snooze.tooltip": "다시 알림",
    "notification.actions.unsnooze.tooltip": "다시 알림 해제",
    "notification.snoozedUntil": "다시 알림 시각",
    "preferences.title": "환경 설정",
    "preferences.emptyNotice": "아직 알림별 환경 설정이 없습니다.",
    "preferences.global": "전체 환경 설정",
    "preferences.schedule.title": "일정",
    "preferences.schedule.description": "다음 시간대에 알림 허용:",
    "preferences.schedule.headerInfo":
        "일정을 설정하세요. 일정 외 시간에는 외부 채널 알림이 일시 중지됩니다. 앱 내 알림과 중요 알림은 항상 전달됩니다.",
    "preferences.schedule.info":
        "중요 알림과 앱 내 알림은 일정 외 시간에도 전달됩니다.",
    "preferences.schedule.days": "요일",
    "preferences.schedule.from": "시작",
    "preferences.schedule.to": "종료",
    "preferences.schedule.copyTimesTo": "시간 복사 대상",
    "preferences.schedule.sunday": "일요일",
    "preferences.schedule.monday": "월요일",
    "preferences.schedule.tuesday": "화요일",
    "preferences.schedule.wednesday": "수요일",
    "preferences.schedule.thursday": "목요일",
    "preferences.schedule.friday": "금요일",
    "preferences.schedule.saturday": "토요일",
    "preferences.schedule.dayScheduleCopy.title": "시간 복사 대상:",
    "preferences.schedule.dayScheduleCopy.selectAll": "모두 선택",
    "preferences.schedule.dayScheduleCopy.apply": "적용",
    "preferences.workflow.disabled.notice":
        "이 중요 알림의 관리를 활성화하려면 관리자에게 문의하세요.",
    "preferences.workflow.disabled.tooltip": "관리자만 수정할 수 있습니다.",
    "preferences.group.info": "이 그룹의 모든 알림에 적용됩니다.",
    "snooze.datePicker.timePickerLabel": "시간",
    "snooze.datePicker.apply": "적용",
    "snooze.datePicker.cancel": "취소",
    "snooze.options.anHourFromNow": "1시간 후",
    "snooze.datePicker.pastDateTooltip": "선택한 시간은 현재로부터 최소 3분 이후여야 합니다.",
    "snooze.datePicker.noDateSelectedTooltip": "날짜를 선택하세요.",
    "snooze.datePicker.exceedingLimitTooltip": ({ days }) =>
        `선택한 시간은 현재로부터 ${days === 1 ? "24시간" : `${days}일`}을 초과할 수 없습니다.`,
    "snooze.options.customTime": "사용자 지정 시간...",
    "snooze.options.inOneDay": "내일",
    "snooze.options.inOneWeek": "다음 주",
    locale: "ko-KR",
};

const novuLocalizationZh: NovuLocalization = {
    "inbox.filters.dropdownOptions.unread": "仅未读",
    "inbox.filters.dropdownOptions.default": "未读和已读",
    "inbox.filters.dropdownOptions.archived": "已归档",
    "inbox.filters.dropdownOptions.snoozed": "已暂缓",
    "inbox.filters.labels.unread": "未读",
    "inbox.filters.labels.default": "收件箱",
    "inbox.filters.labels.archived": "已归档",
    "inbox.filters.labels.snoozed": "已暂缓",
    "notifications.emptyNotice": "暂无新通知，请稍后再查看。",
    "notifications.actions.readAll": "全部标记为已读",
    "notifications.actions.archiveAll": "全部归档",
    "notifications.actions.archiveRead": "归档已读通知",
    "notifications.newNotifications": ({ notificationCount }) =>
        `${notificationCount > 99 ? "99+" : notificationCount} 条新通知`,
    "notification.actions.read.tooltip": "标记为已读",
    "notification.actions.unread.tooltip": "标记为未读",
    "notification.actions.archive.tooltip": "归档",
    "notification.actions.unarchive.tooltip": "取消归档",
    "notification.actions.snooze.tooltip": "暂缓提醒",
    "notification.actions.unsnooze.tooltip": "取消暂缓",
    "notification.snoozedUntil": "暂缓至",
    "preferences.title": "偏好设置",
    "preferences.emptyNotice": "暂无特定通知的偏好设置。",
    "preferences.global": "全局偏好设置",
    "preferences.schedule.title": "计划",
    "preferences.schedule.description": "允许在以下时间接收通知：",
    "preferences.schedule.headerInfo":
        "设置您的计划。在计划时间之外，外部渠道的通知将暂停。应用内通知和重要通知始终会送达。",
    "preferences.schedule.info":
        "在计划时间之外，您仍会收到重要通知和应用内通知。",
    "preferences.schedule.days": "日期",
    "preferences.schedule.from": "从",
    "preferences.schedule.to": "至",
    "preferences.schedule.copyTimesTo": "将时间复制到",
    "preferences.schedule.sunday": "星期日",
    "preferences.schedule.monday": "星期一",
    "preferences.schedule.tuesday": "星期二",
    "preferences.schedule.wednesday": "星期三",
    "preferences.schedule.thursday": "星期四",
    "preferences.schedule.friday": "星期五",
    "preferences.schedule.saturday": "星期六",
    "preferences.schedule.dayScheduleCopy.title": "将时间复制到：",
    "preferences.schedule.dayScheduleCopy.selectAll": "全选",
    "preferences.schedule.dayScheduleCopy.apply": "应用",
    "preferences.workflow.disabled.notice":
        "请联系管理员以启用此重要通知的订阅管理。",
    "preferences.workflow.disabled.tooltip": "仅管理员可编辑",
    "preferences.group.info": "适用于此分组下的所有通知。",
    "snooze.datePicker.timePickerLabel": "时间",
    "snooze.datePicker.apply": "应用",
    "snooze.datePicker.cancel": "取消",
    "snooze.options.anHourFromNow": "一小时后",
    "snooze.datePicker.pastDateTooltip": "所选时间必须至少晚于当前时间 3 分钟。",
    "snooze.datePicker.noDateSelectedTooltip": "请选择日期。",
    "snooze.datePicker.exceedingLimitTooltip": ({ days }) =>
        `所选时间不能超过当前时间起 ${days === 1 ? "24 小时" : `${days} 天`}。`,
    "snooze.options.customTime": "自定义时间...",
    "snooze.options.inOneDay": "明天",
    "snooze.options.inOneWeek": "下周",
    locale: "zh-CN",
};

const NOVU_LOCALIZATIONS: Record<string, NovuLocalization> = {
    de: novuLocalizationDe,
    fr: novuLocalizationFr,
    ko: novuLocalizationKo,
    zh: novuLocalizationZh,
};

/** English (Novu's built-in default) returns undefined. */
export const getNovuLocalization = (language?: string): NovuLocalization | undefined =>
    NOVU_LOCALIZATIONS[(language ?? "").toLowerCase().split("-")[0]];
